// input-tags.tsx

"use client";

import * as React from "react";
import { Badge } from "@/03-presenters/components/ui/badge";
import { Button } from "@/03-presenters/components/ui/button";
import { XIcon } from "lucide-react";
import { type InputProps } from "./input";
import { cn } from "@/lib/utils";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/03-presenters/components/ui/tooltip";
type InputTagsProps = Omit<InputProps, "value" | "onChange"> & {
  value: string[];
  onChange: (value: string[]) => void;
};
const InputTags = React.forwardRef<HTMLInputElement, InputTagsProps>(({
  className,
  value,
  onChange,
  ...props
}, ref) => {
  const [pendingDataPoint, setPendingDataPoint] = React.useState("");
  const [visibleTags, setVisibleTags] = React.useState<string[]>(value);
  const [hiddenTags, setHiddenTags] = React.useState<string[]>([]);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!containerRef.current || !measureRef.current) return;
    const observer = new ResizeObserver(() => {
      if (!containerRef.current || !measureRef.current) return;
      const container = containerRef.current;
      const containerWidth = container.clientWidth - 50; // 预留输入框空间
      let currentWidth = 0;
      const tempVisibleTags: string[] = [];
      for (const tag of value) {
        // 创建临时标签来测量实际宽度
        const tempBadge = document.createElement("div");
        tempBadge.className = "badge-measure";
        tempBadge.innerHTML = `
            <span>${tag}</span>
            <button class="ml-2 h-3 w-3"></button>
          `;
        measureRef.current.appendChild(tempBadge);
        const tagWidth = tempBadge.offsetWidth + 8; // 实际宽度 + 间距
        measureRef.current.removeChild(tempBadge);
        if (currentWidth + tagWidth > containerWidth) {
          setHasOverflow(true);
          break;
        }
        currentWidth += tagWidth;
        tempVisibleTags.push(tag);
      }
      setVisibleTags(tempVisibleTags);
      setHiddenTags(value.filter(tag => !tempVisibleTags.includes(tag)));
      setHasOverflow(tempVisibleTags.length < value.length);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [value]);
  React.useEffect(() => {
    if (pendingDataPoint.includes(",")) {
      const newDataPoints = new Set([...value, ...pendingDataPoint.split(",").map(chunk => chunk.trim())]);
      onChange(Array.from(newDataPoints));
      setPendingDataPoint("");
    }
  }, [pendingDataPoint, onChange, value]);
  const addPendingDataPoint = () => {
    if (pendingDataPoint) {
      const newDataPoints = new Set([...value, pendingDataPoint]);
      onChange(Array.from(newDataPoints));
      setPendingDataPoint("");
    }
  };
  return <>
        <div ref={containerRef} className={cn("flex min-h-7 w-full flex-wrap items-center gap-2 rounded-sm border border-neutral-200 bg-white px-1 text-sm", className)}>
          {visibleTags.map(item => <Badge key={item} variant="secondary" className="flex items-center rounded-[2px] bg-[#F2F5F9] px-1 font-normal text-black hover:bg-[#F2F5F9]">
              {item}
              <Button variant="ghost" size="icon" className="ml-2 h-3 w-3" onClick={() => {
          onChange(value.filter(i => i !== item));
        }}>
                <XIcon className="w-3" color="#666666" />
              </Button>
            </Badge>)}
          {hasOverflow && <TooltipProvider>
              <Tooltip delayDuration={200}>
                <TooltipTrigger>
                  <Badge variant="secondary" className="flex items-center rounded-[2px] bg-[#F2F5F9] px-1 font-normal text-black hover:bg-[#F2F5F9]" title={value.slice(visibleTags.length).join(", ")} // 显示被隐藏的标签
            >
                    +{value.length - visibleTags.length}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="flex max-w-[300px] flex-wrap items-center gap-1 p-1 text-sm" side="top">
                  {hiddenTags.map(item => <Badge key={item} variant="secondary" className="flex items-center rounded-[2px] bg-[#F2F5F9] px-1 font-normal text-black hover:bg-[#F2F5F9]">
                      {item}
                      <Button variant="ghost" size="icon" className="ml-2 h-3 w-3" onClick={() => {
                onChange(value.filter(i => i !== item));
              }}>
                        <XIcon className="w-3" color="#666666" />
                      </Button>
                    </Badge>)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
          <input className="min-w-[50px] flex-1 outline-none placeholder:text-neutral-500 dark:placeholder:text-neutral-400" value={pendingDataPoint} onChange={e => setPendingDataPoint(e.target.value)} onKeyDown={e => {
        if (e.key === "Enter" || e.key === ",") {
          e.preventDefault();
          addPendingDataPoint();
        } else if (e.key === "Backspace" && pendingDataPoint.length === 0 && value.length > 0) {
          e.preventDefault();
          onChange(value.slice(0, -1));
        }
      }} {...props} ref={ref} />
        </div>
        {/* 添加隐藏的测量容器 */}
        <div ref={measureRef} className="fixed -left-[9999px] -top-[9999px] flex" aria-hidden="true">
          <style>
            {`
              .badge-measure {
                display: flex;
                align-items: center;
                padding: 0 0.25rem;
                background: #F2F5F9;
                border-radius: 2px;
                font-size: 0.875rem;
                line-height: 1.25rem;
              }
            `}
          </style>
        </div>
      </>;
});
InputTags.displayName = "InputTags";
export { InputTags };